import { type FieldErrors } from 'react-hook-form';

export interface FlatFieldError {
  /** Dotted form path, e.g. `translations.en.name`. */
  path: string;
  /** RHF's error type — a Zod issue code, or `server`. */
  type: string;
}

function isErrorLeaf(value: Record<string, unknown>): boolean {
  // RHF marks a leaf with `type`; a branch is a plain object of children.
  return typeof value['type'] === 'string' || typeof value['message'] === 'string';
}

/** Every failing path in an RHF error tree, flattened to dotted paths. */
export function flattenErrorPaths(errors: FieldErrors, prefix = ''): FlatFieldError[] {
  const flat: FlatFieldError[] = [];

  for (const [key, value] of Object.entries(errors)) {
    if (value === undefined || value === null || typeof value !== 'object') {
      continue;
    }

    const path = prefix === '' ? key : `${prefix}.${key}`;
    const node = value as Record<string, unknown>;

    if (isErrorLeaf(node)) {
      flat.push({ path, type: typeof node['type'] === 'string' ? node['type'] : 'unknown' });
      continue;
    }

    flat.push(...flattenErrorPaths(node as FieldErrors, path));
  }

  return flat;
}

/**
 * Failing paths that **no control on this form renders**.
 *
 * These are the ones that make Save look broken. A schema can reject something
 * the field list does not cover — a required key the form forgot to include, a
 * cross-field rule, a value carried through from the record being edited — and
 * because no input owns the path, no error appears anywhere. The operator
 * presses Save, nothing happens, and there is nothing on screen to read.
 *
 * Raising them to form level is the same fix `mapServerErrors` applies to server
 * rejections, for the same reason: an error nobody can see is worse than an
 * ugly one.
 */
export function unownedErrorPaths(
  errors: FieldErrors,
  knownPaths: readonly string[],
): FlatFieldError[] {
  const known = new Set(knownPaths);

  return flattenErrorPaths(errors).filter((error) => !known.has(error.path));
}

export interface FieldErrorLeaf {
  type: string;
  message: string;
}

/**
 * Adds one error to an RHF error tree, by dotted path, without mutating it.
 *
 * For failures a **control** knows about and the schema structurally cannot: a
 * `json` editor whose text does not parse never emits a value, so the schema
 * validates the last document that did and reports nothing at all. The resolver
 * folds these in afterwards, which is the only layer where they can survive —
 * `form.setError` is wiped by the resolver on the next submit.
 *
 * **An error already there wins.** The schema looked at the value; this one only
 * knows the text was unreadable, so the schema's message is the more useful of
 * the two and is left in place. Same for a branch: writing through a reported
 * leaf would replace a real error with a container.
 */
export function setErrorAtPath<TErrors extends FieldErrors>(
  errors: TErrors,
  path: string,
  leaf: FieldErrorLeaf,
): TErrors {
  const [head, ...rest] = path.split('.');

  if (head === undefined || head === '') return errors;

  const existing: unknown = (errors as Record<string, unknown>)[head];
  const existingNode =
    typeof existing === 'object' && existing !== null
      ? (existing as Record<string, unknown>)
      : null;

  if (existingNode !== null && isErrorLeaf(existingNode)) {
    return errors;
  }

  const next =
    rest.length === 0
      ? leaf
      : setErrorAtPath((existingNode ?? {}) as FieldErrors, rest.join('.'), leaf);

  return { ...errors, [head]: next } as TErrors;
}
